import { useEffect, useMemo, useRef, useState } from "react";
import { isValidEmail, isValidMobile } from "@utils";

const isErrorStatus = (status) =>
  ["amountTooSmall", "noContactDetails", "noPhone", "noEmail"].includes(status);

export default function useConfirmSendData({
  rows,
  isCustom,
  customAmount,
  smsSelected,
  emailSelected,
  isSingleCustomer,
  selection,
  initializedRef,
  initialContacts = null,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  /* ---- eligible rows only ---- */
  const isRelevantError = isSingleCustomer
    ? (status) => status === "amountTooSmall"
    : isErrorStatus;

  const eligibleRows = useMemo(
    () => (rows?.length ? rows.filter((r) => !isRelevantError(r.status)) : []),
    [rows, isRelevantError],
  );

  /* ---- selection (state owned by parent, persists across steps) ---- */
  const {
    selectedAccounts,
    isRowSelected,
    toggleRowSelection,
    selectAccounts,
    deselectAccounts,
    deselectAll,
    initializeSelection,
  } = selection;

  // Pre-select all eligible rows the first time we encounter a given eligible
  // set. Keyed on the set's signature (not on mount) so navigating back/forth
  // between steps preserves the user's selection, while a genuine change to the
  // eligible accounts (channels toggled, rows re-prepared) re-initialises.
  const eligibleSignature = useMemo(
    () => eligibleRows.map((r) => r.customerId).join("|"),
    [eligibleRows],
  );

  useEffect(() => {
    if (!eligibleRows.length) return;
    if (initializedRef.current === eligibleSignature) return;
    initializeSelection(eligibleRows);
    initializedRef.current = eligibleSignature;
  }, [eligibleSignature]);

  const selectedCount = selectedAccounts.length;

  // Pre-populate contact fields for single-customer sends. The send's own recipients
  // (an edited action step's payload) win over the account row's record details, so a
  // user-stated contact survives the edit round-trip.
  useEffect(() => {
    if (isSingleCustomer && eligibleRows[0]) {
      const email = initialContacts?.email || eligibleRows[0].email || "";
      const phone = initialContacts?.phone || eligibleRows[0].phone || "";
      setContactEmail(email);
      setContactPhone(phone);

      // Focus the first empty contact field
      requestAnimationFrame(() => {
        if (emailSelected && !email) {
          emailInputRef.current?.focus();
        } else if (smsSelected && !phone) {
          phoneInputRef.current?.focus();
        }
      });
    }
  }, [isSingleCustomer]);

  /* ---- search ---- */
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return eligibleRows;
    const term = searchTerm.toLowerCase();
    return eligibleRows.filter((r) =>
      r.customerName?.toLowerCase().includes(term),
    );
  }, [eligibleRows, searchTerm]);

  /* ---- amounts ---- */
  const selectedAmount = useMemo(
    () =>
      isCustom
        ? (parseFloat(customAmount) || 0) * selectedAccounts.length
        : selectedAccounts.reduce(
            (sum, r) => sum + (r.amount || r.minimumAmount || 0),
            0,
          ),
    [selectedAccounts, isCustom, customAmount],
  );

  /* ---- effective accounts (applies single-customer overrides) ---- */
  const effectiveAccounts = useMemo(() => {
    if (!isSingleCustomer) return selectedAccounts;
    return selectedAccounts.map((r) => ({
      ...r,
      ...(emailSelected
        ? { email: contactEmail.trim(), emailError: !isValidEmail(contactEmail) }
        : {}),
      ...(smsSelected
        ? { phone: contactPhone.trim(), phoneError: !isValidMobile(contactPhone) }
        : {}),
    }));
  }, [selectedAccounts, isSingleCustomer, emailSelected, smsSelected, contactEmail, contactPhone]);

  /* ---- channel counts (based on effective rows) ---- */
  const emailCount = useMemo(
    () =>
      emailSelected ? effectiveAccounts.filter((r) => !r.emailError).length : 0,
    [effectiveAccounts, emailSelected],
  );

  const smsCount = useMemo(
    () =>
      smsSelected ? effectiveAccounts.filter((r) => !r.phoneError).length : 0,
    [effectiveAccounts, smsSelected],
  );

  /* ---- live sent amount (derived from row statuses) ---- */
  const sentAmount = useMemo(
    () =>
      isCustom
        ? rows.filter((r) => r.status === "done").length *
          (parseFloat(customAmount) || 0)
        : rows
            .filter((r) => r.status === "done")
            .reduce((sum, r) => sum + (r.amount || r.minimumAmount || 0), 0),
    [rows, isCustom, customAmount],
  );

  const sentCount = useMemo(
    () => rows.filter((r) => r.status === "done").length,
    [rows],
  );

  /* ---- live sent counts (derived from row statuses) ---- */
  const emailsSent = useMemo(
    () =>
      emailSelected
        ? rows.filter((r) => r.status === "done" && !r.emailError).length
        : 0,
    [rows, emailSelected],
  );

  const smsSent = useMemo(
    () =>
      smsSelected
        ? rows.filter((r) => r.status === "done" && !r.phoneError).length
        : 0,
    [rows, smsSelected],
  );

  /* ---- contact validation (single customer) ---- */
  const emailValid = !emailSelected || isValidEmail(contactEmail);
  const phoneValid = !smsSelected || isValidMobile(contactPhone);
  const contactsValid = !isSingleCustomer || (emailValid && phoneValid);

  return {
    // rows & selection
    eligibleRows,
    filteredRows,
    selectedAccounts,
    effectiveAccounts,
    selectedCount,
    isRowSelected,
    toggleRowSelection,
    selectAccounts,
    deselectAccounts,
    deselectAll,
    initializeSelection,

    // search
    searchTerm,
    setSearchTerm,

    // single-customer contacts
    contactEmail,
    setContactEmail,
    contactPhone,
    setContactPhone,
    emailInputRef,
    phoneInputRef,
    emailValid,
    phoneValid,
    contactsValid,

    // counts & amounts
    selectedAmount,
    sentAmount,
    sentCount,
    emailCount,
    smsCount,
    emailsSent,
    smsSent,
  };
}
